/** TS port of discovr's internal/campaigns/amount_parse.go parseAmount —
 *  keep the two in sync by hand (no shared build tooling across the repo
 *  boundary); see amount_parse_test.go / testdata/amount_parse_cases.json
 *  for the cross-language parity fixture. */
export type AmountKind = 'price' | 'metric';

const SUFFIX_MULTIPLIERS: ReadonlyArray<readonly [string, number]> = [
  ['k', 1_000],
  ['m', 1_000_000],
  ['crores', 10_000_000],
  ['crore', 10_000_000],
  ['cr', 10_000_000],
  ['lakhs', 100_000],
  ['lakh', 100_000],
  ['lacs', 100_000],
  ['lac', 100_000],
  ['l', 100_000],
];

const NUMERIC_PATTERN = /^\d+(\.\d+)?$/;

/** Gate applied only to the metric path's bare `Number(rest)` call. The
 *  metric path is deliberately lenient (accepts scientific notation, e.g.
 *  "1e3") in a way the strict price path isn't — but JS's `Number()` also
 *  accepts non-decimal literal syntax ("0x10" -> 16, "0b101" -> 5,
 *  "0o17" -> 15) that Go's strconv.ParseFloat does not (no binary/octal
 *  float support at all, and hex requires a "p" exponent). Without this
 *  gate, a metric cell like "0x10" would parse successfully here but be
 *  rejected server-side — a parity gap against this file's "mirrors the
 *  backend exactly" contract.
 *
 *  Accepts plain decimal and decimal-with-exponent, with an optional
 *  leading sign — ParseFloat accepts a leading "+"/"-", and the "-" case is
 *  still caught by the n<0 check below, so this pattern must not reject it.
 *  Also accepts a bare-decimal form on either side (".5" or "5.") — Go's
 *  ParseFloat(".5",64) and ParseFloat("5.",64) both succeed (verified via
 *  `go run`), so a tighter \d+(\.\d+)? here would be a REVERSE parity gap:
 *  rejecting input the backend would accept.
 *
 *  Known, accepted divergence: Go's ParseFloat also accepts single
 *  underscore digit-separators ("1_000" -> 1000), but JS's `Number()`
 *  does not (Number("1_000") is NaN — separators are a source-literal
 *  syntax, not a string-parsing one) — closing that gap needs stripping
 *  underscores before Number(), a separate code path, not just this regex.
 *  Not worth it: nobody types "1_000" as a view count in a spreadsheet. */
const METRIC_NUMERIC_PATTERN = /^[+-]?(?:\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;

/** Defensive cap on a sheet cell's raw length before it reaches the
 *  regexes/Number() below. Not a real risk today (no catastrophic-
 *  backtracking regex here, and Number() parsing is linear), but raw is
 *  untrusted user-uploaded spreadsheet data, and no legitimate price/metric
 *  cell is anywhere close to this long. Mirrors the backend's
 *  maxRawLength. */
const MAX_RAW_LENGTH = 64;

/** Generous sanity ceiling on any sheet-import number (price or metric) —
 *  real creator deal prices, view counts, and engagement rates never
 *  approach this. Purpose is defense-in-depth: closes the gap where a
 *  legitimate-looking but extreme finite value (e.g. scientific notation on
 *  the deliberately-lenient metric path, or an absurd all-digit price)
 *  could overflow unguarded downstream aggregation (roster reach/cost
 *  totals) even though the finite check below lets it through as "valid."
 *  Mirrors the backend's maxParsedAmount. */
const MAX_PARSED_AMOUNT = 1_000_000_000; // 1 billion

/** Strips Unicode "Currency Symbol" runes (category Sc — ₹ $ € £ ¥ ₮ ₸ ₼ ₾
 *  ₭ ¤ and every other currency symbol) plus a hardcoded whitespace list —
 *  comma, tab, newline, CR, literal space. Deliberately NOT JS's broader
 *  `\s` class (which also matches NBSP U+00A0 and others not in the
 *  backend's list): matching this exact set keeps parity with the Go
 *  side, including both sides' shared (intentional) failure to handle an
 *  NBSP-only-separated number. */
function stripCurrencySymbols(s: string): string {
  return s
    .replace(/\p{Sc}/gu, '')
    .replace(/[,\t\n\r ]/g, '')
    .trim();
}

function stripSuffixMultiplier(s: string): { rest: string; mult: number } {
  for (const [suffix, mult] of SUFFIX_MULTIPLIERS) {
    if (s.endsWith(suffix)) {
      return { rest: s.slice(0, -suffix.length), mult };
    }
  }
  return { rest: s, mult: 1 };
}

/** Parses a free-text sheet cell into a number, mirroring the backend's
 *  parseAmount exactly. 'price' is strict (\d+(\.\d+)? only, no scientific
 *  notation) and sign-aware (preserves a leading "-"); 'metric' is lenient
 *  (accepts scientific notation) and rejects negative instead — this
 *  asymmetry is intentional, matching the backend's own split. Checks for a
 *  non-finite result twice: right after parsing the bare number, and again
 *  after applying the suffix multiplier, since a huge-but-finite number
 *  times a multiplier can overflow to Infinity. */
export function parseAmount(raw: string, kind: AmountKind): number | null {
  if (raw.length > MAX_RAW_LENGTH) return null;

  let s = stripCurrencySymbols(raw.trim().toLowerCase());

  let neg = false;
  if (kind === 'price') {
    s = s.replace(/\/[-=]*$/, '');
    neg = s.startsWith('-');
    if (neg) s = s.slice(1);
  } else {
    s = s.endsWith('%') ? s.slice(0, -1) : s;
  }

  const { rest, mult } = stripSuffixMultiplier(s);

  if (kind === 'price' && !NUMERIC_PATTERN.test(rest)) return null;
  if (kind === 'metric' && !METRIC_NUMERIC_PATTERN.test(rest)) return null;
  if (rest === '') return null;

  const n = Number(rest);
  if (!Number.isFinite(n)) return null;
  if (kind === 'metric' && n < 0) return null;

  const amount = n * mult;
  if (!Number.isFinite(amount) || Math.abs(amount) > MAX_PARSED_AMOUNT) return null;

  return neg ? -amount : amount;
}
